import React, { useState } from "react"
import Formsy from "formsy-react"
import types from "prop-types"
import { useSubmit } from "hooks"
import * as API from "services/api"
import { FormsyTextInput } from "shared/inputs"
import { PrimaryButton } from "shared/buttons"
import { errorToast, successToast } from "shared/toast"
import Tooltip from "shared/Tooltip"

const USER_UPDATE_SUCCESS_MESSAGE = "User updated successfully!"

const USER_UPDATE_ERROR_MESSAGE = "Something went wrong. Unable to update user."

const EditForm = ({ user, setUser }) => {
  const { canSubmit, enableSubmit, disableSubmit } = useSubmit()

  // resets form to most recent valid state, vs pristine which is from page load
  const [pristineKey, setPristineKey] = useState(Date.now())

  const updateUser = async (userAttrs) => {
    const changedAttrs = Object.entries(userAttrs).reduce((acc, [key, value]) => {
      if (user[key] !== value) {
        acc[key] = value
      }
      return acc
    }, {})

    const response = await API.updateUser({ userId: user.id, user: changedAttrs })
    if (response.ok) {
      setUser({ ...user, ...changedAttrs })
      successToast(USER_UPDATE_SUCCESS_MESSAGE)
    } else {
      setPristineKey(Date.now())
      errorToast(USER_UPDATE_ERROR_MESSAGE, response)
    }
  }

  const emailUpdateable = user.identityUpdateableByCurrentIdentity
  const tooltipText = emailUpdateable ? "" : "Cannot update email address. Email support@performancehealth.app for assistance."

  return (
    <Formsy
      key={pristineKey}
      onSubmit={updateUser}
      onValid={enableSubmit}
      onInvalid={disableSubmit}
      preventDefaultSubmit
    >
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          <div className="flex-center flex-col lg:flex-row gap-4 w-full">
            <div className="w-full">
              <label htmlFor="user-first_name-input">First Name</label>
              <FormsyTextInput
                id="user-first_name-input"
                className="bordered-base-input text-sm"
                name="first_name"
                placeholder="First Name ..."
                value={user.first_name}
                required
                validations={{ isNotBlankString: true }}
                validationErrors={{ isDefaultRequiredValue: "required" }}
              />
            </div>

            <div className="w-full">
              <label htmlFor="user-last_name-input">Last Name</label>
              <FormsyTextInput
                id="user-last_name-input"
                className="bordered-base-input text-sm"
                name="last_name"
                placeholder="Last Name ..."
                value={user.last_name}
                required
                validations={{ isNotBlankString: true }}
                validationErrors={{ isDefaultRequiredValue: "required" }}
              />
            </div>
          </div>

          <div>
            <Tooltip text={tooltipText}>
              <label htmlFor="user-email-input">Email</label>
              <FormsyTextInput
                id="user-email-input"
                className="bordered-base-input text-sm"
                name="email"
                placeholder="Email ..."
                value={user.email}
                required
                disabled={!emailUpdateable}
                validations={{ isNotBlankString: true, isEmail: true }}
                validationErrors={{ isDefaultRequiredValue: "required", isEmail: "Invalid email address" }}
              />
            </Tooltip>
          </div>
        </div>

        <PrimaryButton
          className="w-full lg:max-w-max lg:self-center"
          text="Save changes"
          type="submit"
          disabled={!canSubmit}
        />
      </div>
    </Formsy>
  )
}

EditForm.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  user: types.object.isRequired,
  setUser: types.func.isRequired,
}

export default EditForm
